"use client";

import { useState, useEffect, useCallback } from "react";

interface TokenLogosResult {
  /** Look up a logo URL by token symbol (case-insensitive). Returns null if not found. */
  getLogoBySymbol: (symbol: string) => string | null;
  /** Whether the manifest has been loaded. */
  loaded: boolean;
}

/** Hardcoded fallbacks for tokens that may not appear in the manifest (e.g. testnet). */
const FALLBACK_LOGOS: Record<string, string> = {
  USDC: "/icons/tokens/usdc.png",
  XLM: "/token-logos/xlm.png",
  NATIVE: "/token-logos/xlm.png",
  AQUA: "/icons/tokens/aqua.jpg",
  PHO: "/icons/tokens/phoenix.png",
};

/**
 * Shared hook for resolving token logos by symbol.
 *
 * Fetches `/token-logos.json` once (manifest keyed by contract ID),
 * builds a secondary `bySymbol` index by parsing the symbol from each
 * logo filename (e.g. `/token-logos/USDC-GA5ZSE...-CCW67T....svg` → "USDC").
 *
 * Falls back to hardcoded paths for common tokens not in the manifest.
 */
export function useTokenLogos(): TokenLogosResult {
  const [bySymbol, setBySymbol] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch("/token-logos.json", { signal: AbortSignal.timeout(5_000) })
      .then((r) => r.json())
      .then((data: { logos?: Record<string, string> }) => {
        const logos = data.logos ?? {};
        const map: Record<string, string> = {};

        for (const [, logoPath] of Object.entries(logos)) {
          // logoPath format: /token-logos/SYMBOL-ISSUER-CONTRACT.ext
          const filename = logoPath.split("/").pop() ?? "";
          const symbol = filename.split("-")[0]?.toUpperCase();
          if (symbol && !map[symbol]) {
            map[symbol] = logoPath;
          }
        }

        setBySymbol(map);
      })
      .catch(() => { /* silent — fallback logos used */ })
      .finally(() => { setLoaded(true); });
  }, []);

  const getLogoBySymbol = useCallback(
    (symbol: string): string | null => {
      const key = symbol.toUpperCase();
      return bySymbol[key] ?? FALLBACK_LOGOS[key] ?? null;
    },
    [bySymbol],
  );

  return { getLogoBySymbol, loaded };
}
